// The banner that opens a stop on the path. One button: a locked place still
// opens its sheet, so the learner sees where they are heading.
import type { Taken } from "@aybuben/client/device/polaroid-memory.ts";
import type { PlaceCardModel } from "@aybuben/client/places/cards.ts";
import { accentOf } from "@aybuben/client/world/regions.ts";
import type { LangId } from "@aybuben/core/lang.ts";
import { Lock } from "lucide-react";

import { useStrings, type UiKey } from "../../i18n/index.tsx";
import { ARM, COL, FOCUS, HEX } from "../../theme.ts";
import { CardArtSlot, PolaroidSlot } from "./art.tsx";

interface PlaceCardProps {
	model: PlaceCardModel;
	revealing: boolean;
	lang: LangId;
	taken: Taken | null;
	onOpen: () => void;
}

export function PlaceCard({ model, revealing, lang, taken, onOpen }: PlaceCardProps) {
	const t = useStrings();
	const { stop, state, here, meta, aria } = model;
	const locked = state === "locked";
	const art = ["place-art", locked && "place-art-locked", revealing && "place-art-reveal"]
		.filter(Boolean)
		.join(" ");
	return (
		<button
			type="button"
			data-testid="place-card"
			data-stop={stop.id}
			onClick={onOpen}
			// placeCardsOf always sets params.state to a STATE_KEY value, which is a UiKey.
			aria-label={t(aria.key, { ...aria.params, state: t(aria.params.state as UiKey) })}
			className={`mt-9 w-full overflow-hidden rounded-3xl text-left ${FOCUS}`}
			style={{
				background: COL.card,
				border: `2px solid ${COL.line}`,
				boxShadow: `0 6px 0 ${COL.edge}`,
			}}
		>
			<div style={{ position: "relative" }}>
				<div className={art}>
					<CardArtSlot placeId={stop.id} accent={accentOf(stop.section?.id ?? null)} />
				</div>
				{here && (
					<span
						className="rounded-full text-[11px] font-extrabold"
						// Over the art, which keeps the world palette in either theme: the pill does too.
						style={{
							position: "absolute",
							top: 10,
							left: 10,
							padding: "3px 9px",
							background: "#fff",
							color: HEX.pom,
							border: `2px solid ${HEX.line}`,
						}}
					>
						{t("place.here")}
					</span>
				)}
				{state === "done" && (
					<div style={{ position: "absolute", top: 8, right: 10 }}>
						<PolaroidSlot stop={stop} taken={taken} lang={lang} size={40} tilt={6} />
					</div>
				)}
			</div>
			<div style={{ padding: "10px 14px 12px" }}>
				<div
					data-testid="place-meta"
					className="flex items-center gap-1 uppercase"
					style={{
						fontSize: 10,
						fontWeight: 800,
						letterSpacing: ".06em",
						color: locked ? COL.faint : state === "done" ? COL.apricotDark : COL.pom,
					}}
				>
					{locked && <Lock size={14} aria-hidden />}
					{t(meta.key, meta.params)}
				</div>
				<div className="flex flex-wrap items-baseline gap-x-2">
					<span
						style={{
							fontFamily: ARM,
							fontSize: 22,
							fontWeight: 700,
							color: locked ? COL.faint : COL.ink,
						}}
					>
						{stop.place.hy}
					</span>
					<span style={{ fontSize: 14, fontWeight: 800, color: locked ? COL.faint : COL.mute }}>
						{stop.place.name}
					</span>
				</div>
			</div>
		</button>
	);
}
